import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import {
  AngularFireStorage,
  AngularFireUploadTask
} from '@angular/fire/compat/storage';
import firebase from 'firebase/compat/app';
import { TranslateService } from '@ngx-translate/core';
import { Subscribable } from 'src/shared/components/base-component/subscribable';
import { FavieAuthService } from 'src/shared/services/auth/favie-auth.service';

@Injectable()
export class FirebaseService extends Subscribable {
  constructor(
    private angularFireAuth: AngularFireAuth,
    private storage: AngularFireStorage,
    private favieAuthService: FavieAuthService,
    private readonly translate: TranslateService,
  ) {
    super();

    this.subscribe(this.angularFireAuth.authState, (user) => {
      if (user) {
        this.angularFireAuth.updateCurrentUser(user);
      }
    });
  }

  public createUserWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> {
    return this.angularFireAuth.createUserWithEmailAndPassword(email, password);
  }

  public signInWithEmailAndPassword(
    email: string,
    password: string
  ): Promise<firebase.auth.UserCredential> {
    return this.angularFireAuth.signInWithEmailAndPassword(email, password);
  }

  public signOut() {
    this.angularFireAuth.signOut();
  }

  public getIdToken() {
    return this.angularFireAuth.idToken;
  }

  public async uploadFile(file: File, path?: string): Promise<string> {
    const uid = this.favieAuthService.getUserUid();
    // The storage path
    path = path || `${uid}/${Date.now()}_${file.name}`;

    // Reference to storage bucket
    const ref = this.storage.ref(path);
    // The main task
    const task: AngularFireUploadTask = this.storage.upload(path, file);
    const taskResult = await task.snapshotChanges().toPromise();
    const url = await taskResult.ref.getDownloadURL();
    return url;
  }

  public getFirebaseError(error: any) {
    if (typeof error === 'string') {
      return error;
    }
    const translate = error?.code ? this.translate.instant(error.code) : null;
    if (translate && translate !== error.code) {
      error.message = translate;
    }
    return error;
  }

  public async changePassword(newPassword: string) {
    return (await this.angularFireAuth.currentUser).updatePassword(newPassword);
  }

  public async updateEmail(newEmail: string) {
    return (await this.angularFireAuth.currentUser).updateEmail(newEmail);
  }

  public async applyActionCode(code: string) {
    await this.angularFireAuth.applyActionCode(code);
  }

  public downloadEvidence(folderName: string, fileName: string) {
    const files = this.storage.ref(`${folderName}/${fileName}/`);
    return files;
  }

  public async getCurrentUser() {
    return await this.angularFireAuth.currentUser;
  }
}
